<template>
  <div v-if="supported" class="ai-ask-resource-block">
    <DmsPlayerEmbed
      v-if="isMedia && mediaVid"
      :key="mediaVid + '-' + resourceType"
      :vid="mediaVid"
      :variant="playerVariant"
    />
    <div v-else-if="isMedia && !mediaVid" class="resource-fallback">
      媒体资源不可用
      <div v-if="displayName" class="resource-name">{{ displayName }}</div>
    </div>
    <div v-else-if="isRichText" class="resource-card">
      <div v-if="displayName" class="resource-name resource-name--title">{{ displayName }}</div>
      <div v-if="richHtml" class="resource-rich rich-text" v-html="richHtml"></div>
      <div v-else class="resource-hint">富文本内容暂不可用</div>
    </div>
    <div v-else-if="isNavigable" class="resource-card">
      <a
        v-if="navHref"
        class="resource-link"
        :href="navHref"
        target="_blank"
        rel="noopener noreferrer"
        @click.prevent="openResource"
      >{{ displayName }}</a>
      <span v-else class="resource-name">{{ displayName }}</span>
    </div>
    <div v-if="isMedia && mediaVid && displayName" class="resource-caption">{{ displayName }}</div>
  </div>
</template>

<script>
import DmsPlayerEmbed from "./DmsPlayerEmbed.vue";
import { renderAssistantMarkdown } from "@/utils/ai-ask-resource-parse";
import {
  isInlineMediaResourceType,
  isInlineRichTextResourceType,
  isNavigableResourceType,
  isRobotSupportedResourceType,
  parseSubjectResourceType,
  SubjectResourceType,
} from "@/utils/subject-resource-type";

export default {
  name: "AiAskResourceBlock",
  components: { DmsPlayerEmbed },
  props: {
    resourceId: {
      type: String,
      default: "",
    },
    name: {
      type: String,
      default: "",
    },
    mediaId: {
      type: String,
      default: "",
    },
    resourceType: {
      type: [Number, String],
      default: 0,
    },
    goodsId: {
      type: String,
      default: "",
    },
    richContent: {
      type: String,
      default: "",
    },
    linkUrl: {
      type: String,
      default: "",
    },
  },
  computed: {
    tagType() {
      return parseSubjectResourceType(this.resourceType);
    },
    supported() {
      return isRobotSupportedResourceType(this.tagType);
    },
    displayName() {
      return (this.name || "资源").trim();
    },
    isMedia() {
      return isInlineMediaResourceType(this.tagType);
    },
    isRichText() {
      return isInlineRichTextResourceType(this.tagType);
    },
    isNavigable() {
      return isNavigableResourceType(this.tagType);
    },
    mediaVid() {
      return (this.mediaId || "").trim();
    },
    playerVariant() {
      return this.tagType === SubjectResourceType.Audio ? "audio" : "video";
    },
    richHtml() {
      const raw = (this.richContent || "").trim();
      if (!raw) return "";
      if (/<[a-z][\s\S]*>/i.test(raw)) return raw;
      return renderAssistantMarkdown(raw);
    },
    navHref() {
      const url = (this.linkUrl || "").trim();
      if (url) return url;
      if (this.goodsId && this.resourceId) {
        return (
          "/#/resourcesRead?goodsId=" +
          encodeURIComponent(this.goodsId.trim())
        );
      }
      return "";
    },
  },
  methods: {
    openResource() {
      const href = this.navHref;
      if (!href) return;
      if (/^https?:\/\//i.test(href)) {
        window.open(href, "_blank", "noopener,noreferrer");
        return;
      }
      window.open(href, "_blank");
    },
  },
};
</script>

<style scoped>
.ai-ask-resource-block {
  margin: 8px 0;
}

.resource-card,
.resource-fallback {
  padding: 10px 12px;
  border: 1px solid var(--ai-rt-divider, #eee);
  border-radius: 8px;
  background: rgba(0, 0, 0, 0.02);
  font-size: 13px;
  color: var(--ai-rt-font-secondary, #666);
}

.resource-name {
  font-size: 13px;
  color: var(--ai-rt-font-primary, #333);
  margin-top: 4px;
  word-break: break-word;
}

.resource-name--title {
  margin-top: 0;
  margin-bottom: 8px;
  font-weight: 500;
}

.resource-caption {
  margin-top: 4px;
  font-size: 12px;
  color: #888;
  word-break: break-word;
}

.resource-link {
  color: var(--ai-rt-primary, #409eff);
  text-decoration: none;
  word-break: break-all;
}

.resource-link:hover {
  text-decoration: underline;
}

.resource-rich {
  word-break: break-word;
}

.resource-hint {
  font-size: 12px;
  color: #999;
}
</style>
